/**
 * Duplicate Image Detection Utilities
 * ตรวจสอบและป้องกันรูปภาพซ้ำ
 */

import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ตรวจสอบรูปภาพซ้ำโดยใช้ hash ของไฟล์
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<boolean>} - true ถ้าเป็นรูปซ้ำ
 */
export const checkDuplicateImage = async (file, girlId) => {
  try {
    // สร้าง hash ของไฟล์
    const fileHash = await generateFileHash(file);
    
    // ตรวจสอบในฐานข้อมูล
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return false;
    }
    
    const girlData = girlDoc.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    // ตรวจสอบ hash ที่ซ้ำ
    const duplicateImage = allImages.find(img => img.fileHash === fileHash);
    
    if (duplicateImage) {
      console.log('Duplicate image found:', duplicateImage.fileName);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking duplicate image:', error);
    return false;
  }
};

/**
 * สร้าง hash ของไฟล์
 * @param {File} file - ไฟล์ที่ต้องการสร้าง hash
 * @returns {Promise<string>} - hash ของไฟล์
 */
export const generateFileHash = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const arrayBuffer = reader.result;
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * ตรวจสอบรูปภาพซ้ำโดยใช้ขนาดไฟล์และชื่อ
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<boolean>} - true ถ้าเป็นรูปซ้ำ
 */
export const checkDuplicateBySizeAndName = async (file, girlId) => {
  try {
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return false;
    }
    
    const girlData = girlDoc.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    // ตรวจสอบขนาดไฟล์และชื่อที่ซ้ำ
    const duplicateImage = allImages.find(img => 
      img.originalSize === file.size && 
      img.originalName === file.name
    );
    
    if (duplicateImage) {
      console.log('Duplicate image found by size and name:', duplicateImage.fileName);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking duplicate by size and name:', error);
    return false;
  }
};

/**
 * ตรวจสอบรูปภาพซ้ำโดยใช้ URL
 * @param {string} imageUrl - URL ของรูปภาพ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<boolean>} - true ถ้าเป็นรูปซ้ำ
 */
export const checkDuplicateByUrl = async (imageUrl, girlId) => {
  try {
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return false;
    }
    
    const girlData = girlDoc.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    // ตรวจสอบ URL ที่ซ้ำ
    const duplicateImage = allImages.find(img => img.url === imageUrl);
    
    if (duplicateImage) {
      console.log('Duplicate image found by URL:', duplicateImage.fileName);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking duplicate by URL:', error);
    return false;
  }
};

/**
 * ตรวจสอบรูปภาพซ้ำแบบครอบคลุม
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{isDuplicate: boolean, reason: string, duplicateImage: object}>}
 */
export const checkAllDuplicateMethods = async (file, girlId) => {
  try {
    // ตรวจสอบโดยใช้ hash
    const isHashDuplicate = await checkDuplicateImage(file, girlId);
    if (isHashDuplicate) {
      return {
        isDuplicate: true,
        reason: 'hash',
        duplicateImage: null
      };
    }
    
    // ตรวจสอบโดยใช้ขนาดและชื่อ
    const isSizeNameDuplicate = await checkDuplicateBySizeAndName(file, girlId);
    if (isSizeNameDuplicate) {
      return {
        isDuplicate: true,
        reason: 'size_and_name',
        duplicateImage: null
      };
    }
    
    return {
      isDuplicate: false,
      reason: 'none',
      duplicateImage: null
    };
  } catch (error) {
    console.error('Error checking all duplicate methods:', error);
    return {
      isDuplicate: false,
      reason: 'error',
      duplicateImage: null
    };
  }
};

/**
 * ลบรูปภาพซ้ำออกจากฐานข้อมูล
 * @param {string} girlId - ID ของผู้ใช้
 * @param {string} duplicateImageId - ID ของรูปภาพซ้ำ
 * @returns {Promise<boolean>} - true ถ้าลบสำเร็จ
 */
export const removeDuplicateImage = async (girlId, duplicateImageId) => {
  try {
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return false;
    }
    
    const girlData = girlDoc.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    // หารูปภาพซ้ำ
    const duplicateImage = allImages.find(img => img.id === duplicateImageId);
    if (!duplicateImage) {
      return false;
    }
    
    // ลบออกจาก array ที่เหมาะสม
    let updateData = {};
    
    if (duplicateImage.status === 'approved') {
      updateData.images = girlData.images.filter(img => img.id !== duplicateImageId);
    } else if (duplicateImage.status === 'pending') {
      updateData.pendingImages = girlData.pendingImages.filter(img => img.id !== duplicateImageId);
    } else if (duplicateImage.status === 'rejected') {
      updateData.rejectedImages = girlData.rejectedImages.filter(img => img.id !== duplicateImageId);
    }
    
    // อัพเดทฐานข้อมูล
    await updateDoc(girlsRef, {
      ...updateData,
      lastUpdated: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error removing duplicate image:', error);
    return false;
  }
};

/**
 * สร้างรายงานรูปภาพซ้ำ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<Array>} - รายการรูปภาพซ้ำ
 */
export const generateDuplicateReport = async (girlId) => {
  try {
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return [];
    }
    
    const girlData = girlDoc.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    // หารูปภาพซ้ำ
    const duplicates = [];
    const seenHashes = new Set();
    const seenSizes = new Set();
    
    allImages.forEach((image, index) => {
      const hash = image.fileHash || `${image.originalSize}_${image.originalName}`;
      const sizeKey = `${image.originalSize}_${image.originalName}`;
      
      if (seenHashes.has(hash) || seenSizes.has(sizeKey)) {
        duplicates.push({
          ...image,
          duplicateIndex: index,
          duplicateReason: seenHashes.has(hash) ? 'hash' : 'size_and_name'
        });
      } else {
        seenHashes.add(hash);
        seenSizes.add(sizeKey);
      }
    });
    
    return duplicates;
  } catch (error) {
    console.error('Error generating duplicate report:', error);
    return [];
  }
};

/**
 * ทำความสะอาดรูปภาพซ้ำ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<number>} - จำนวนรูปภาพที่ลบออก
 */
export const cleanupDuplicateImages = async (girlId) => {
  try {
    const duplicates = await generateDuplicateReport(girlId);
    
    if (duplicates.length === 0) {
      return 0;
    }
    
    const girlsRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlsRef);
    
    if (!girlDoc.exists()) {
      return 0;
    }
    
    const girlData = girlDoc.data();
    
    // ลบรูปภาพซ้ำออกจาก arrays
    const cleanedImages = girlData.images.filter(img => 
      !duplicates.some(dup => dup.id === img.id)
    );
    const cleanedPendingImages = girlData.pendingImages.filter(img => 
      !duplicates.some(dup => dup.id === img.id)
    );
    const cleanedRejectedImages = girlData.rejectedImages.filter(img => 
      !duplicates.some(dup => dup.id === img.id)
    );
    
    // อัพเดทฐานข้อมูล
    await updateDoc(girlsRef, {
      images: cleanedImages,
      pendingImages: cleanedPendingImages,
      rejectedImages: cleanedRejectedImages,
      lastUpdated: new Date().toISOString()
    });
    
    return duplicates.length;
  } catch (error) {
    console.error('Error cleaning up duplicate images:', error);
    return 0;
  }
};
